/** Cast, pricing and seed content for the demo. Ported from the design doc. */

export type Term = 'm' | 'y' | 'y2'

export type Person = {
  key: string
  name: string
  email: string
  initials: string
  tint: string
  role: string
  /** Has accepted the invite. */
  active: boolean
}

export type Device = {
  name: string
  /** The manager's own handset — never requestable. */
  own?: boolean
  /** Vito's phone, which the demo drives through the live accept/decline flow. */
  vito?: boolean
  /** Starts out already sharing. */
  sharing?: boolean
}

export type ThreatKind = 'site' | 'call'

/**
 * A blocked threat, a protection the member switched off, or a dark-web
 * breach found against their account.
 */
export type AlertKind = ThreatKind | 'feature' | 'breach'

export type Threat = {
  kind: AlertKind
  subject: string
  title: string
  detail: string
  time: string
  device: string
  /** Set when kind is 'feature': which protection went off. */
  feature?: FeatureId
  /**
   * Set when kind is 'breach': whose account it was. Dark Web Monitor works on
   * the account, not the handset, so this survives even where device
   * attribution does not.
   */
  account?: string
  /** The manager has already turned it back on. */
  resolved?: boolean
}

export type FeatureId = 'maliciousSite' | 'scamCall' | 'adTracker'

export type Feature = {
  id: FeatureId
  name: string
  /** Icon basename under /assets/icons. */
  icon: string
  /** How the manager sees it described once it is off. */
  offDetail: string
}

/** Threat Protection Pro toggles, as they appear on the member's Protect tab. */
export const FEATURES: Record<FeatureId, Feature> = {
  maliciousSite: {
    id: 'maliciousSite',
    name: 'Malicious website blocker',
    icon: 'malicious-site',
    offDetail: 'Phishing and malware pages will load normally until this is switched back on.',
  },
  scamCall: {
    id: 'scamCall',
    name: 'Scam call protection',
    icon: 'spam-call',
    offDetail: 'Known scam numbers will ring through until this is switched back on.',
  },
  adTracker: {
    id: 'adTracker',
    name: 'Ad and tracker blocker',
    icon: 'threat-protection',
    offDetail: 'Trackers will follow this device between sites until this is switched back on.',
  },
}

export const FEATURE_ORDER: FeatureId[] = ['maliciousSite', 'scamCall', 'adTracker']

export const MANAGER = {
  key: 'self',
  name: 'Michael Corleone (You)',
  email: 'michael@corleone.com',
  role: 'Dad · manager',
  initials: 'M',
} as const

export const INVITEE: Omit<Person, 'role' | 'active'> = {
  key: 'vito',
  name: 'Vito Corleone',
  email: 'vito@corleone.com',
  initials: 'V',
  tint: 'var(--chart-6)',
}

export const FAMILY: Person[] = [
  {
    key: 'katherine',
    name: 'Katherine Adams-Corleone',
    email: 'katherine@corleone.com',
    initials: 'K',
    tint: 'var(--chart-3)',
    role: 'Wife · 4 devices',
    active: true,
  },
  {
    key: 'anthony',
    name: 'Anthony Corleone',
    email: 'anthony@corleone.com',
    initials: 'A',
    tint: 'var(--chart-7)',
    role: 'Son · 2 devices',
    active: true,
  },
]

export const DEVICES: Record<string, Device[]> = {
  self: [{ name: "Michael's iPhone 15 Pro", own: true }],
  vito: [{ name: "Vito's iPhone", vito: true }],
  katherine: [{ name: "Katherine's MacBook Air", sharing: true }, { name: "Katherine's iPad" }],
  anthony: [{ name: "Anthony's iPhone" }, { name: "Anthony's Apple TV" }],
}

export const SEED: Threat = {
  kind: 'site',
  subject: 'invoice-lt-pay.click',
  title: 'Malicious site blocked',
  detail: 'Phishing page imitating an invoice portal. Stopped before it loaded.',
  time: '2 days ago',
  device: "Katherine's MacBook Air",
}

export const SEATS = 5

type PriceRow = { basic: number; complete: number; group: number; months: number; label: string }

export const TABLE: Record<Term, PriceRow> = {
  m: { basic: 14.99, complete: 19.99, group: 47.99, months: 1, label: 'billed monthly' },
  y: { basic: 5.49, complete: 6.49, group: 21.99, months: 12, label: 'for the first year' },
  y2: { basic: 3.49, complete: 4.49, group: 19.99, months: 24, label: 'for the first 2 years' },
}

export const TERM_LABEL: Record<Term, string> = { m: '1 month', y: '1 year', y2: '2 years' }

/**
 * When the Group plan renews, by term.
 *
 * Group is bought outright rather than folded into the existing subscription,
 * so it renews on its own date — not the Complete plan's 28 Aug. One source for
 * both the checkout line and the success screen, so they cannot disagree.
 */
export const RENEWAL_DATE: Record<Term, string> = {
  m: '19 Sep 2026',
  y: '19 Aug 2027',
  y2: '19 Aug 2028',
}

export const eur = (n: number) => '€' + n.toFixed(2)

export type Breach = {
  service: string
  leaked: string
  /** When the dump itself is dated, not when we found it. */
  when: string
}

/** The breach the demo trigger surfaces against the member's account. */
export const MEMBER_BREACH: Breach = {
  service: 'driveline-fitness.com',
  leaked: 'Email address, password',
  when: 'Dump dated March 2026',
}

/**
 * Dark Web Monitor hit. Deliberately carries an account rather than a device:
 * it is found by matching the member's email against a dump, so the group can
 * attribute it without knowing anything about their handsets.
 */
export function makeBreachAlert(account: string, breach: Breach = MEMBER_BREACH): Threat {
  return {
    kind: 'breach',
    account,
    subject: breach.service,
    title: 'Credentials found in a breach',
    detail: `${breach.leaked} from ${breach.service} turned up in a dark web dump.`,
    time: 'now',
    device: account,
  }
}

/** The alert the manager receives when a member switches a protection off. */
export function makeFeatureAlert(feature: FeatureId, device: string): Threat {
  return {
    kind: 'feature',
    feature,
    subject: FEATURES[feature].name,
    title: `${FEATURES[feature].name} turned off`,
    detail: FEATURES[feature].offDetail,
    time: 'now',
    device,
  }
}

/** Builds the threat a demo trigger raises on the member's handset. */
export function makeThreat(kind: ThreatKind, device: string): Threat {
  return kind === 'call'
    ? {
        kind: 'call',
        subject: '+370 6 214 8890',
        title: 'Scam call blocked',
        detail: 'Reported for delivery-fee scams. The call never rang.',
        time: 'now',
        device,
      }
    : {
        kind: 'site',
        subject: 'lt-parcel-pay.top',
        title: 'Malicious site blocked',
        detail: 'Phishing page imitating a parcel tracker. Stopped before it loaded.',
        time: 'now',
        device,
      }
}
